import { useState } from 'react'
import { Plus } from 'lucide-react'
import type { IsbtTemplate } from '@/api/labelPrintingApi'
import { useIsbtTemplates } from '@/hooks/useLabelPrinting'
import {
  DATE_FORMAT_LABEL,
  USE_LABEL,
  isbtTemplateStatus,
  labelSizeText,
} from '@/lib/labelPrinting'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { Button } from '@/components/ui/button'
import { TableCell } from '@/components/ui/table'
import { IsbtTemplateDialog } from './IsbtTemplateDialog'
import { ListSection, ListTable, NameCell, StatusChip } from './shared'

const TABLE_ID = 'label-printing-isbt-templates'

const COLUMNS: TableColumnDef[] = [
  { id: 'displayName', header: 'Display name', locked: true, defaultWidth: 220, sortable: true },
  { id: 'fileName', header: 'File name', defaultWidth: 200, sortable: true },
  { id: 'labelSize', header: 'Label size', defaultWidth: 120, sortable: true },
  { id: 'use', header: 'Use', defaultWidth: 140, sortable: true },
  { id: 'dateFormat', header: 'Date format', defaultWidth: 120, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 190, sortable: true },
]

const SORT: SortAccessors<IsbtTemplate> = {
  displayName: t => t.displayName,
  fileName: t => t.fileName,
  labelSize: t => t.labelSize,
  use: t => USE_LABEL[t.use],
  dateFormat: t => DATE_FORMAT_LABEL[t.dateFormat],
  status: t => isbtTemplateStatus(t).label,
}

/**
 * ISBT label designs (z8nrz7evkc). Smart CGT stores only the file name; the
 * label program on the print computer owns the layout. A printer can use a
 * template only once it is approved after a checked test print.
 */
export function IsbtTemplatesList({
  canManage,
  onNotice,
}: {
  canManage: boolean
  onNotice: (message: string) => void
}) {
  const query = useIsbtTemplates()
  // null is a new template; an id is one being edited.
  const [open, setOpen] = useState<{ id: string | null } | null>(null)
  // Read from the list, so an edit shows the saved row after every refetch.
  const editing = open?.id ? query.data?.find(t => t.id === open.id) : undefined

  return (
    <>
      <ListSection
        title='ISBT Templates'
        blurb='One row per label design file on the print computer (HemaTrax with NiceLabel). Smart CGT stores only the file name. Printers pick from this list.'
        aside={
          canManage && (
            <Button onClick={() => setOpen({ id: null })}>
              <Plus className='mr-2 h-4 w-4' />
              Add template
            </Button>
          )
        }
        footnote='A printer can use a template only after a test label is printed, checked and approved.'
        query={query}
        noun='templates'
      >
        {rows => (
          <ListTable
            tableId={TABLE_ID}
            columns={COLUMNS}
            sort={SORT}
            rows={rows}
            cells={t => ({
              displayName: (
                <TableCell>
                  <NameCell name={t.displayName} onOpen={canManage ? () => setOpen({ id: t.id }) : undefined} />
                </TableCell>
              ),
              fileName: <TableCell className='font-mono text-xs'>{t.fileName}</TableCell>,
              labelSize: (
                <TableCell className={t.labelSize ? undefined : 'text-muted-foreground'}>
                  {labelSizeText(t.labelSize)}
                </TableCell>
              ),
              use: <TableCell>{USE_LABEL[t.use]}</TableCell>,
              dateFormat: <TableCell>{DATE_FORMAT_LABEL[t.dateFormat]}</TableCell>,
              status: (
                <TableCell>
                  <StatusChip {...isbtTemplateStatus(t)} />
                </TableCell>
              ),
            })}
          />
        )}
      </ListSection>

      {open && (open.id === null || editing) && (
        <IsbtTemplateDialog
          key={open.id ?? 'new'}
          template={editing}
          onClose={() => setOpen(null)}
          onDone={message => {
            setOpen(null)
            onNotice(message)
          }}
        />
      )}
    </>
  )
}
